import { useState, useEffect, useCallback } from "react";
import {
  getFinanzasGlobalResumen,
  getFinanzasGlobalHistorial,
} from "../lib/api";
import {
  Button, Input, FormField,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  PageHeader, StatCard, Spinner, Card, CardContent, CardHeader, CardTitle,
} from "../components/ui";
import { TrendingUp, Wrench, ShoppingBag, Landmark, DollarSign, BarChart2 } from "lucide-react";

function fmt(n) {
  return Number(n || 0).toLocaleString("es-PY");
}

const MESES_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

function labelMes(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `${MESES_ES[parseInt(m, 10) - 1]} ${y}`;
}

// ─── Barra de progreso compacta ──────────────────────────────────────────────
function MiniBar({ value, max, color = "bg-primary" }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Celda con valor monetario coloreado ─────────────────────────────────────
function MoneyCell({ value, positive = true, dim = false }) {
  if (!value && value !== 0) return <TableCell className="text-right text-muted-foreground">—</TableCell>;
  const color = dim ? "text-muted-foreground" : positive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500";
  return (
    <TableCell className={`text-right font-mono text-xs ${color}`}>
      {fmt(value)}
    </TableCell>
  );
}

// ─── Tarjeta fuente con desglose ─────────────────────────────────────────────
function FuenteCard({ title, icon: Icon, color, children }) {
  return (
    <Card className={`border-l-4 ${color}`}>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-1.5 text-sm">
        {children}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-mono font-medium text-xs tabular-nums">
        {value}
        {sub && <span className="text-muted-foreground font-normal ml-1">{sub}</span>}
      </span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════
export default function FinanzasTotales() {
  const [resumen,   setResumen]   = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [desde,     setDesde]     = useState("");
  const [hasta,     setHasta]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (desde) p.desde = desde;
      if (hasta) p.hasta = hasta;
      const [r, h] = await Promise.all([
        getFinanzasGlobalResumen(p),
        getFinanzasGlobalHistorial(p),
      ]);
      setResumen(r.data);
      setHistorial(h.data);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  useEffect(() => { load(); }, [load]);

  const maxIngresos = historial.length
    ? Math.max(...historial.map((m) => m.totales.ingresos))
    : 1;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="px-4 pt-4">
        <PageHeader title="Finanzas Totales" sub="Consolidado de Taller · Bazar · Préstamos" />
      </div>

      <div className="flex flex-col gap-5 px-4 pb-8">
        {/* ── Filtros ── */}
        <div className="flex flex-wrap gap-3 items-end">
          <FormField label="Desde">
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" />
          </FormField>
          <FormField label="Hasta">
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" />
          </FormField>
          <Button variant="outline" onClick={() => { setDesde(""); setHasta(""); }}>
            Limpiar filtros
          </Button>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* ── KPIs globales ── */}
            {resumen && (
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <StatCard
                  title="Ingresos totales"
                  value={`Gs. ${fmt(resumen.totales.ingresos)}`}
                  sub="Taller + Bazar + Comisiones"
                  icon={DollarSign}
                />
                <StatCard
                  title="Costos"
                  value={`Gs. ${fmt(resumen.totales.costos)}`}
                  sub="Bazar + IVA cheques"
                  icon={TrendingUp}
                />
                <StatCard
                  title="Ganancia estimada"
                  value={`Gs. ${fmt(resumen.totales.ganancia)}`}
                  sub="Ingresos netos"
                  icon={TrendingUp}
                />
                <StatCard
                  title="Operaciones"
                  value={
                    (resumen.taller.facturas || 0) +
                    (resumen.bazar.ventas || 0) +
                    (resumen.prestamos.liquidaciones || 0)
                  }
                  sub="Cobros + Ventas + Liquidaciones"
                  icon={BarChart2}
                />
              </div>
            )}

            {/* ── Desglose por fuente ── */}
            {resumen && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FuenteCard title="Cobros del Taller" icon={Wrench} color="border-blue-500">
                  <Row label="Facturas cobradas" value={resumen.taller.facturas} />
                  <Row label="Ingresos" value={`Gs. ${fmt(resumen.taller.ingresos)}`} />
                </FuenteCard>

                <FuenteCard title="Bazar" icon={ShoppingBag} color="border-amber-500">
                  <Row label="Ventas" value={resumen.bazar.ventas} />
                  <Row label="Ingresos" value={`Gs. ${fmt(resumen.bazar.ingresos)}`} />
                  <Row label="Costos" value={`Gs. ${fmt(resumen.bazar.costos)}`} />
                  <Row label="Ganancia" value={`Gs. ${fmt(resumen.bazar.ganancia)}`} />
                </FuenteCard>

                <FuenteCard title="Préstamos / Cheques" icon={Landmark} color="border-violet-500">
                  <Row label="Liquidaciones" value={resumen.prestamos.liquidaciones} />
                  <Row label="Comisión" value={`Gs. ${fmt(resumen.prestamos.comision)}`} />
                  <Row label="Gastos admin." value={`Gs. ${fmt(resumen.prestamos.gastos_admin)}`} />
                  <Row label="IVA recaudado" value={`Gs. ${fmt(resumen.prestamos.iva)}`} />
                </FuenteCard>
              </div>
            )}

            {/* ── Historial mensual ── */}
            {historial.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">
                Sin datos para el período seleccionado.
              </p>
            ) : (
              <Card>
                <CardHeader className="px-4 pt-4 pb-2">
                  <CardTitle className="text-base">Desglose mensual</CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-4 min-w-[90px]">Mes</TableHead>
                          {/* Taller */}
                          <TableHead className="text-right text-blue-600 dark:text-blue-400">
                            <div className="text-[10px] font-semibold uppercase tracking-wider">Taller</div>
                            <div className="text-xs font-normal">Cobros</div>
                          </TableHead>
                          {/* Bazar */}
                          <TableHead className="text-right text-amber-600 dark:text-amber-400">
                            <div className="text-[10px] font-semibold uppercase tracking-wider">Bazar</div>
                            <div className="text-xs font-normal">Ingresos</div>
                          </TableHead>
                          <TableHead className="text-right text-amber-600 dark:text-amber-400">
                            <div className="text-[10px] font-semibold uppercase tracking-wider opacity-0">·</div>
                            <div className="text-xs font-normal">Costos</div>
                          </TableHead>
                          <TableHead className="text-right text-amber-600 dark:text-amber-400">
                            <div className="text-[10px] font-semibold uppercase tracking-wider opacity-0">·</div>
                            <div className="text-xs font-normal">Ganancia</div>
                          </TableHead>
                          {/* Préstamos */}
                          <TableHead className="text-right text-violet-600 dark:text-violet-400">
                            <div className="text-[10px] font-semibold uppercase tracking-wider">Cheques</div>
                            <div className="text-xs font-normal">Comisión</div>
                          </TableHead>
                          {/* Totales */}
                          <TableHead className="text-right border-l border-border">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-foreground">Total</div>
                            <div className="text-xs font-normal">Ingresos</div>
                          </TableHead>
                          <TableHead className="text-right pr-4">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-foreground">Total</div>
                            <div className="text-xs font-normal">Ganancia</div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historial.map((m) => (
                          <TableRow key={m.mes}>
                            <TableCell className="pl-4">
                              <div className="font-medium text-sm">{labelMes(m.mes)}</div>
                              <MiniBar
                                value={m.totales.ingresos}
                                max={maxIngresos}
                                color="bg-primary"
                              />
                            </TableCell>
                            {/* Taller */}
                            <MoneyCell value={m.taller.ingresos} dim={m.taller.facturas === 0} />
                            {/* Bazar */}
                            <MoneyCell value={m.bazar.ingresos} dim={m.bazar.ventas === 0} />
                            <MoneyCell value={m.bazar.costos} positive={false} dim={m.bazar.ventas === 0} />
                            <MoneyCell value={m.bazar.ganancia} dim={m.bazar.ventas === 0} />
                            {/* Cheques */}
                            <MoneyCell value={m.prestamos.comision} dim={m.prestamos.liquidaciones === 0} />
                            {/* Totales */}
                            <TableCell className="text-right border-l border-border font-mono font-semibold text-sm">
                              {fmt(m.totales.ingresos)}
                            </TableCell>
                            <TableCell className="text-right pr-4 font-mono font-semibold text-sm text-emerald-600 dark:text-emerald-400">
                              {fmt(m.totales.ganancia)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Fila de totales al pie */}
                  {historial.length > 1 && (() => {
                    const tot = historial.reduce(
                      (acc, m) => ({
                        t_ing:  acc.t_ing  + m.taller.ingresos,
                        b_ing:  acc.b_ing  + m.bazar.ingresos,
                        b_cost: acc.b_cost + m.bazar.costos,
                        b_gan:  acc.b_gan  + m.bazar.ganancia,
                        p_com:  acc.p_com  + m.prestamos.comision,
                        ing:    acc.ing    + m.totales.ingresos,
                        gan:    acc.gan    + m.totales.ganancia,
                      }),
                      { t_ing: 0, b_ing: 0, b_cost: 0, b_gan: 0, p_com: 0, ing: 0, gan: 0 }
                    );
                    return (
                      <div className="border-t border-border bg-muted/40">
                        <Table>
                          <TableBody>
                            <TableRow className="font-semibold hover:bg-transparent">
                              <TableCell className="pl-4 min-w-[90px] text-xs uppercase tracking-wider text-muted-foreground">
                                Total período
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">{fmt(tot.t_ing)}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{fmt(tot.b_ing)}</TableCell>
                              <TableCell className="text-right font-mono text-xs text-rose-500">{fmt(tot.b_cost)}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{fmt(tot.b_gan)}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{fmt(tot.p_com)}</TableCell>
                              <TableCell className="text-right border-l border-border font-mono font-bold">{fmt(tot.ing)}</TableCell>
                              <TableCell className="text-right pr-4 font-mono font-bold text-emerald-600 dark:text-emerald-400">{fmt(tot.gan)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
